/**
 * Centralized Auth Access Hook
 *
 * Provides authentication state and token retrieval for API calls.
 * Auth0 handles identity/authentication only. Authorization (roles,
 * permissions) is backend DB-driven and accessed via /api/me — NOT
 * from JWT token claims.
 *
 * This hook does NOT expose permissions or hasPermission(). For
 * authorization decisions, use useCurrentUser() (which reads from
 * /api/me) or gate on backend API responses.
 */

import { useCallback, useRef } from "react"
import { useAuth0 } from "@auth0/auth0-react"

/**
 * Decode a JWT payload without signature verification.
 * Client-side only — used to extract sub for observability.
 */
function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const parts = token.split(".")
    if (parts.length !== 3) return null
    const payload = parts[1]
    const base64 = payload.replace(/-/g, "+").replace(/_/g, "/")
    const padded = base64 + "=".repeat((4 - (base64.length % 4)) % 4)
    const decoded = atob(padded)
    return JSON.parse(decoded)
  } catch {
    return null
  }
}

export interface AuthAccessResult {
  /** Whether the user is authenticated */
  isAuthenticated: boolean
  /** Whether auth state is still loading */
  isLoading: boolean
  /** Trigger Auth0 login flow */
  login: (returnTo?: string) => void
  /** Trigger Auth0 logout flow */
  logout: () => void
  /** Get access token for API calls (async, may refresh) */
  getAccessToken: () => Promise<string | null>
  /** User subject identifier (opaque, for observability) */
  userSub: string | null
}

// Module-level token cache. Serves two purposes:
//   (1) Fast-path: subsequent API calls within the token's lifetime return the
//       cached token synchronously-ish, avoiding Auth0 library overhead that
//       was showing up as ~500–800ms "Queueing" in DevTools on each request.
//   (2) Dedup: if multiple requests fire concurrently (e.g. /api/me and
//       /api/companies on page load), they share one in-flight promise instead
//       of each racing Auth0.
// Expiry is derived from the JWT `exp` claim (minus a 60s safety margin) so
// the cache respects actual token lifetime rather than a guessed TTL.
const FALLBACK_TTL_MS = 5 * 60 * 1000
const EXPIRY_SAFETY_MS = 60 * 1000
let cachedToken: string | null = null
let cachedSub: string | null = null
let cachedExpiry = 0
let inFlight: Promise<string | null> | null = null

/**
 * Centralized auth access hook.
 *
 * Usage:
 *   const { isAuthenticated, getAccessToken, userSub } = useAuthAccess()
 *
 * For authorization (admin checks, role display), use:
 *   useCurrentUser() — reads from /api/me
 *   getAdapter().getMe() — reads from /api/me
 */
export function useAuthAccess(): AuthAccessResult {
  const {
    isAuthenticated,
    isLoading,
    loginWithRedirect,
    logout: auth0Logout,
    getAccessTokenSilently,
  } = useAuth0()

  // Ref tracks latest isAuthenticated so the token getter callback can read
  // the current value without being recreated (avoids stale-closure in the
  // module-level tokenGetter registered by ApiAuthBridge).
  const isAuthenticatedRef = useRef(isAuthenticated)
  isAuthenticatedRef.current = isAuthenticated

  const login = useCallback(
    (returnTo?: string) => {
      loginWithRedirect({
        appState: { returnTo: returnTo || window.location.pathname },
      })
    },
    [loginWithRedirect]
  )

  const logout = useCallback(() => {
    cachedToken = null
    cachedSub = null
    cachedExpiry = 0
    inFlight = null

    auth0Logout({
      logoutParams: {
        returnTo: window.location.origin,
      },
    })
  }, [auth0Logout])

  const getAccessToken = useCallback(async (): Promise<string | null> => {
    if (!isAuthenticatedRef.current) return null

    const now = Date.now()
    // Fast path: cached token still valid.
    if (cachedToken && now < cachedExpiry) return cachedToken
    // Concurrent path: another caller is already fetching; share the promise.
    if (inFlight) return inFlight

    const promise = (async () => {
      try {
        const token = await getAccessTokenSilently()
        const payload = decodeJwtPayload(token)
        cachedToken = token
        cachedSub = payload && typeof payload.sub === "string" ? payload.sub : null
        const expSec = payload && typeof payload.exp === "number" ? payload.exp : null
        cachedExpiry = expSec
          ? expSec * 1000 - EXPIRY_SAFETY_MS
          : Date.now() + FALLBACK_TTL_MS
        return token
      } catch (error) {
        cachedToken = null
        cachedExpiry = 0
        const msg = error instanceof Error ? error.message : "unknown error"
        console.error("[useAuthAccess] Failed to get access token:", msg)
        return null
      } finally {
        inFlight = null
      }
    })()

    inFlight = promise
    return promise
  }, [getAccessTokenSilently])

  return {
    isAuthenticated,
    isLoading,
    login,
    logout,
    getAccessToken,
    userSub: cachedSub,
  }
}
